import type { MapSlotDef, MineInstance } from '../types'
import { MineFieldGraph } from './MineFieldGraph'
import { isSolvable } from './solvability'

/** Базовая длительность empty channel (×3 применяется в DefuseSM). */
export const BASE_EMPTY_CHANNEL_SEC = 2.0
export const MINE_CHANNEL_MIN_SEC = 1.2
export const MINE_CHANNEL_MAX_SEC = 3.5
/** Отступ от края тайла для offset мины. */
export const MINE_OFFSET_MARGIN = 0.15

export interface GenerateResult {
  graph: MineFieldGraph
  seed: number
  mineIds: string[]
  mines: MineInstance[]
  attempts: number
}

/** Детерминированный RNG (mulberry32) */
export function createRng(seed: number): () => number {
  let t = seed >>> 0
  return () => {
    t += 0x6d2b79f5
    let r = Math.imul(t ^ (t >>> 15), 1 | t)
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r)
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296
  }
}

function pickMines(
  ids: string[],
  count: number,
  rng: () => number,
  exclude: Set<string>,
): string[] {
  const pool = ids.filter((id) => !exclude.has(id))
  const chosen: string[] = []
  const copy = [...pool]
  const n = Math.min(count, copy.length)
  for (let i = 0; i < n; i += 1) {
    const idx = Math.floor(rng() * copy.length)
    chosen.push(copy[idx]!)
    copy.splice(idx, 1)
  }
  return chosen
}

function buildMineInstances(
  mineIds: string[],
  slots: MapSlotDef[],
  tileSize: number,
  rng: () => number,
): MineInstance[] {
  const byId = new Map(slots.map((s) => [s.id, s]))
  const half = tileSize / 2 - MINE_OFFSET_MARGIN
  const span = Math.max(0.05, half)

  return mineIds.map((slotId) => {
    // Offset в плоскости тайла, clamp ±(tileSize/2 − margin)
    const ox = (rng() * 2 - 1) * span
    const oz = (rng() * 2 - 1) * span
    const duration =
      MINE_CHANNEL_MIN_SEC +
      rng() * (MINE_CHANNEL_MAX_SEC - MINE_CHANNEL_MIN_SEC)
    void byId.get(slotId)
    return {
      slotId,
      offset: { x: ox, z: oz },
      channelDurationSec: duration,
    }
  })
}

export function generateLayout(
  slots: MapSlotDef[],
  mineCount: number,
  seed: number,
  options?: { maxAttempts?: number; safeStartId?: string; tileSize?: number },
): GenerateResult {
  const maxAttempts = options?.maxAttempts ?? 80
  const tileSize = options?.tileSize ?? 1.2
  const rng = createRng(seed)
  const ids = slots.map((s) => s.id)
  const exclude = new Set<string>()
  if (options?.safeStartId) exclude.add(options.safeStartId)

  let attempts = 0
  let lastMines: string[] = []
  let lastInstances: MineInstance[] = []

  while (attempts < maxAttempts) {
    attempts += 1
    const mineIds = pickMines(ids, mineCount, rng, exclude)
    const mines = buildMineInstances(mineIds, slots, tileSize, rng)
    lastMines = mineIds
    lastInstances = mines
    const graph = new MineFieldGraph(slots)
    graph.resetMines(mineIds, mines)
    if (isSolvable(graph, options?.safeStartId)) {
      return { graph, seed, mineIds, mines, attempts }
    }
  }

  // Fallback: последняя раскладка (лучше играбельно, чем пустой fail)
  const graph = new MineFieldGraph(slots)
  graph.resetMines(lastMines, lastInstances)
  return { graph, seed, mineIds: lastMines, mines: lastInstances, attempts }
}

export function pickMineCount(min: number, max: number, seed: number): number {
  const rng = createRng(seed ^ 0x9e3779b9)
  const lo = Math.min(min, max)
  const hi = Math.max(min, max)
  return lo + Math.floor(rng() * (hi - lo + 1))
}

/** Empty channel = BASE × 3 (штраф за пустой поиск). */
export function emptyChannelDurationSec(): number {
  return BASE_EMPTY_CHANNEL_SEC * 3
}
